import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {ReturnRateWeeeSystemData} from '../../../../models/return-rate-weee-system-data.interface';
import {Chart} from 'angular-highcharts';
import {RETURN_BY_YEAR, RETURN_BY_YEAR_OWN_USER, RETURN_TREND_BY_COUNTRY} from '../../../../../constants/charts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {ReturnRateWeeeSystemInfo} from '../../../../models/return-rate-weee-system-info.interface';
import {MessageService} from '../../../../../shared/services/message.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

@Component({
  selector: 'app-return-rate-benchmark-by-year-chart',
  templateUrl: './return-rate-benchmark-by-year-chart.component.html',
  styleUrls: ['./return-rate-benchmark-by-year-chart.component.scss']
})
export class ReturnRateBenchmarkByYearChartComponent implements OnInit, OnChanges {

  @Input()
  data!: ReturnRateWeeeSystemInfo;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;

  textBelow!: string;
  chart!: Chart;
  textBelowCode = 'kfcharts.WeeeSystem.ReturnRate.BenchmarkByYear.textBelow';
  isAdmin!: boolean;
  sort!: string | null;

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private readonly route: ActivatedRoute,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    this.sort = this.route.snapshot.queryParamMap.get('order');
    if (this.data) {
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: RETURN_BY_YEAR,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              allowOverlap: true,
              format: `{point.custom.label}{point.y:.2f}%`
            }
          }
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return [this.point.name];
              }
              return ['<div class="center"><b>' + this.point.name + ' - ' + formatNumber(this.point.y) + '% </b></div><br><br>',
                '- Rank: ' + pointOptions.custom?.rank + '<br>',
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          labels: {
            enabled: false
          }
        },
        series: this.convertToChartData(this.data.chartData)
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'], {state: {code: this.textBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }

  private convertToChartData(data: ReturnRateWeeeSystemData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    if (this.sort == 'asc') {
      data.sort((a, b) => a.returnRate - b.returnRate);
    } else {
      data.sort((a, b) => b.returnRate - a.returnRate);
    }
    chartData.push({
      name: this.data.year.toString(),
      type: 'column',
      data: data.map((item, index) => {
        const systemSplited = item.weeeSystem.split(' ');
        let system = '';
        systemSplited.forEach(value => {
          system = system + (value + '<br/>');
        });

        return {
          y: Number(item.returnRate.toFixed(2)),
          name: item.weeeSystem === 'System ' ? item.weeeSystem + (index + 1) : item.weeeSystem,
          color: item.weeeSystem === 'System ' ? RETURN_BY_YEAR[0] : RETURN_BY_YEAR_OWN_USER[0],
          custom: {rank: index + 1, label: (item.weeeSystem === 'System ' ? '' : system)}
        };
      })
    });
    chartData.push({
      name: this.data.weightedAverage ? `Weighted Average (${formatNumber(this.data.weightedAverage)} %)` : 'Weighted Average ()',
      type: 'area',
      marker: {
        enabled: false
      },
      color: RETURN_TREND_BY_COUNTRY[1],
      enableMouseTracking: false,
      data: data.map(() => this.data.weightedAverage ? Number(this.data.weightedAverage.toFixed(2)) : 0)
    });
    chartData.push({
      name: this.data.average ? `Average (${formatNumber(this.data.average)} %)` : 'Average ()',
      type: 'line',
      marker: {
        enabled: false
      },
      color: RETURN_TREND_BY_COUNTRY[3],
      enableMouseTracking: false,
      data: data.map(() => Number(this.data.average.toFixed(2)))
    });

    return chartData;
  }

}
